// Palette offered by the "Add colour" picker on task cards.
//
// The colour is painted as a bar down the card's RIGHT edge, not as its
// background. That changed what is safe to offer: when the colour filled the
// card, red and green were off-limits because they collided with "overdue"
// and "done", and every swatch had to be a pale tint to keep body text
// readable. As a 6px edge neither constraint applies, so the list now ends
// with saturated primaries alongside the original soft tints.
//
// The BANNED_HUE_RANGES below are kept for the tint section and for the test
// that guards it; they no longer restrict what a user can pick, since the
// spectrum tab offers the full 24-bit RGB range.

export const CARD_COLORS = [
  { name: "Amber", hex: "#fde68a" },
  { name: "Peach", hex: "#fed7aa" },
  { name: "Sand", hex: "#f5e6c8" },
  { name: "Butter", hex: "#fef3c7" },

  { name: "Rose", hex: "#fbcfe8" },
  { name: "Blush", hex: "#f8d2e0" },
  { name: "Mauve", hex: "#e9d5ff" },
  { name: "Orchid", hex: "#f0d0f7" },

  { name: "Violet", hex: "#d9c9f7" },
  { name: "Plum", hex: "#e4d4f4" },
  { name: "Teal", hex: "#99e6e0" },
  { name: "Aqua", hex: "#bfeae6" },

  { name: "Stone", hex: "#e7e5e4" },
  { name: "Clay", hex: "#e0d8d1" },
  { name: "Fog", hex: "#e6e3e0" },
  { name: "Linen", hex: "#f0ece7" },

  // Saturated primaries, safe now that the colour is a 6px edge rather than
  // the card's background: at that size they read as a marker, not as the
  // "done" / "overdue" status tints that fill a whole card.
  { name: "Red", hex: "#ef4444" },
  { name: "Orange", hex: "#f97316" },
  { name: "Green", hex: "#22c55e" },
  { name: "Blue", hex: "#3b82f6" },
];

// Hue ranges (HSL degrees) that are off-limits, used by the test below and by
// any future addition to the palette.
const BANNED_HUE_RANGES = [
  [95, 165], // greens
  [200, 260], // blues
  [345, 360], // reds (upper wrap)
  [0, 12], // reds (lower wrap)
];

// The complement of BANNED_HUE_RANGES: the hue arcs the spectrum may offer.
// Together these cover 200° of the 360° wheel.
//   13–94    yellows, ambers, oranges (past red, short of green)
//   166–199  teals and cyans (past green, short of blue)
//   261–344  purples, violets, magentas, pinks (past blue, short of red)
export const ALLOWED_HUE_ARCS = [
  [13, 94],
  [166, 199],
  [261, 344],
];

const ARC_SPAN = ALLOWED_HUE_ARCS.reduce((sum, [lo, hi]) => sum + (hi - lo), 0);

/**
 * Maps a 0..1 slider position onto the allowed arcs only, so the banned hues
 * simply do not exist on the track — the user cannot land on a red, green or
 * blue by dragging, rather than being blocked after the fact.
 */
export const positionToHue = (t) => {
  const clamped = Math.max(0, Math.min(1, t));
  let remaining = clamped * ARC_SPAN;
  for (const [lo, hi] of ALLOWED_HUE_ARCS) {
    const span = hi - lo;
    if (remaining <= span) return lo + remaining;
    remaining -= span;
  }
  const [, lastHi] = ALLOWED_HUE_ARCS[ALLOWED_HUE_ARCS.length - 1];
  return lastHi;
};

/** Inverse of positionToHue — finds the slider position for a given hue. */
export const hueToPosition = (hue) => {
  let travelled = 0;
  for (const [lo, hi] of ALLOWED_HUE_ARCS) {
    if (hue >= lo && hue <= hi) return (travelled + (hue - lo)) / ARC_SPAN;
    travelled += hi - lo;
  }
  return 0;
};

export const hslToHex = (h, s, l) => {
  const a = (s / 100) * Math.min(l / 100, 1 - l / 100);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const c = l / 100 - a * Math.max(-1, Math.min(k - 3, Math.min(9 - k, 1)));
    return Math.round(255 * c)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

/**
 * CSS gradient stops for the spectrum track. Built from the allowed arcs so
 * the rendered bar matches exactly what the slider can select — no visible
 * band of colour that turns out to be unpickable.
 */
export const buildSpectrumGradient = (s, l) => {
  const stops = [];
  const STEPS = 48;
  for (let i = 0; i <= STEPS; i += 1) {
    const t = i / STEPS;
    stops.push(`${hslToHex(positionToHue(t), s, l)} ${(t * 100).toFixed(1)}%`);
  }
  return `linear-gradient(to right, ${stops.join(", ")})`;
};

export const hexToHsl = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;

  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  const l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  return { h, s: s * 100, l: l * 100 };
};

// A near-grey swatch has almost no hue, so it can sit anywhere on the wheel
// without reading as blue/green/red. Only saturated colours are hue-checked.
export const isBannedHue = (hex) => {
  const { h, s } = hexToHsl(hex);
  if (s < 18) return false; // effectively neutral
  return BANNED_HUE_RANGES.some(([lo, hi]) => h >= lo && h <= hi);
};

export default CARD_COLORS;
